import { FieldDropdown } from 'blockly/core';

import { beginnerDeviceField, getBeginnerDeviceOptions } from '../devices/device-selector.js';
import type { BeginnerDeviceOption } from '../devices/types.js';

/** Shared position modes for OLED (and future TFT) text/cursor blocks. */
export const DISPLAY_POSITION_LINE_COLUMN = 'LINE_COLUMN';
export const DISPLAY_POSITION_XY = 'XY';

export const OLED_ADDRESS_OPTIONS: [string, string][] = [
  ['0x3C', '0x3C'],
  ['0x3D', '0x3D'],
];

export const OLED_TEXT_SIZE_OPTIONS: [string, string][] = [
  ['1', '1'],
  ['2', '2'],
  ['3', '3'],
  ['4', '4'],
  ['5', '5'],
];

export const OLED_FILL_COLOR_OPTIONS: [string, string][] = [
  ['White', 'WHITE'],
  ['Black', 'BLACK'],
];

/** OLED device dropdown — board catalog label (e.g. OLED 1). */
export function oledDeviceField(name = 'DEVICE') {
  return beginnerDeviceField(name, 'oled');
}

/** Runtime FieldDropdown for JS-defined OLED blocks. */
export function createOledDeviceDropdown(): FieldDropdown {
  return new FieldDropdown((): BeginnerDeviceOption[] => getBeginnerDeviceOptions('oled'));
}

/** Position mode: Line/Column vs X/Y (JSON field shape). */
export function oledPositionModeField(name = 'POSITION_MODE') {
  return {
    type: 'field_dropdown',
    name,
    options: [
      ['Line / Column', DISPLAY_POSITION_LINE_COLUMN],
      ['X / Y', DISPLAY_POSITION_XY],
    ],
  };
}

/** Line 1–8 (SSD1306 text rows at size 1). */
export function oledLineField(name = 'LINE', value = 1) {
  return {
    type: 'field_number',
    name,
    value,
    min: 1,
    max: 8,
    precision: 1,
  };
}

/** Column 1–21 (SSD1306 text columns at size 1). */
export function oledColumnField(name = 'COLUMN', value = 1) {
  return {
    type: 'field_number',
    name,
    value,
    min: 1,
    max: 21,
    precision: 1,
  };
}

/** Pixel X 0–127. */
export function oledXField(name = 'X', value = 0) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: 127,
    precision: 1,
  };
}

/** Pixel Y 0–63. */
export function oledYField(name = 'Y', value = 0) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: 63,
    precision: 1,
  };
}

/** Non-negative size field (width / height / radius). */
export function oledSizeField(name: string, value = 10, max = 128) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max,
    precision: 1,
  };
}
